// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    suggestions: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    // XNG-Change: add suggestion
    goToUrl: (url: string, track: boolean, suggestion: string) => void;
    extendedStyles?: AutocompleteSuggestionsStyles;
    // XNG-Change
    highlightedIndex: number;
}

export interface AutocompleteSuggestionsStyles {
    headerText?: TypographyPresentationProps;
    divider?: InjectableCss;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
}

export const autocompleteSuggestionStyles: AutocompleteSuggestionsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    divider: {
        css: css`
            min-width: 100%;
        `,
    },
    link: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
        `,
    },
    focusedLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 5px 0;
            `,
        },
        css: css`
            width: 100%;
            overflow: hidden;
            background-color: lightgray;
        `,
    },
};

const styles = autocompleteSuggestionStyles;

// const Divider = styled.hr`
//     ${styles.divider?.css}
// `;

class AutocompleteSuggestions extends React.Component<Props> {
    private readonly styles: AutocompleteSuggestionsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { suggestions } = this.props;
        if (!suggestions || suggestions.length === 0) {
            return null;
        }

        const styles = this.styles;
        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteSuggestions_Header"
                    className="autocomplete-suggestion-header"
                >
                    {translate("Suggestions")}
                </Typography>
                {suggestions.map((suggestion: any, index: number) => (
                    <Link
                        {...(this.props.focusedItem === suggestion || this.props.highlightedIndex === index
                            ? styles.focusedLink
                            : styles.link)}
                        key={suggestion.suggestion}
                        onClick={() => {
                            this.props.goToUrl(`/Search?query=${suggestion.suggestion}`, true, suggestion.suggestion);
                        }}
                        data-test-selector="autocompleteSuggestions_Result"
                        className="autocomplete-suggestion-link"
                    >
                        {suggestion.suggestion}
                    </Link>
                ))}
            </>
        );
    }
}

export default AutocompleteSuggestions;
